import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { format } from "date-fns";
import { Space, Button, Dropdown, Menu, Modal, Select, Tag, Popover, Typography, Upload, message, Row, Col } from "antd";
import { UploadOutlined, DownloadOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import Papa from "papaparse";
import type { ColumnsType } from "antd/es/table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlug,
  faBatteryFull,
  faBatteryQuarter,
  faBatteryHalf,
  faBatteryThreeQuarters,
} from "@fortawesome/free-solid-svg-icons";

import {
  Application,
  ListApplicationDeviceProfilesResponse,
  ApplicationDeviceProfileListItem,
  ListApplicationDeviceTagsResponse,
  ApplicationDeviceTagListItem,
} from "@chirpstack/chirpstack-api-grpc-web/api/application_pb";
import type { ListDevicesResponse, DeviceListItem } from "@chirpstack/chirpstack-api-grpc-web/api/device_pb";
import { 
  Device, 
  CreateDeviceRequest, 
  GetDeviceRequest, 
  GetDeviceResponse,
  GetDeviceKeysRequest,
  GetDeviceKeysResponse,
  DeviceKeys,
  CreateDeviceKeysRequest,
  DeleteDeviceRequest
} from "@chirpstack/chirpstack-api-grpc-web/api/device_pb";
import { DeviceActivation, ActivateDeviceRequest, GetDeviceActivationRequest, GetDeviceActivationResponse } from "@chirpstack/chirpstack-api-grpc-web/api/device_pb";
import {
  ListApplicationDeviceProfilesRequest,
  ListApplicationDeviceTagsRequest,
} from "@chirpstack/chirpstack-api-grpc-web/api/application_pb";
import { ListDevicesRequest } from "@chirpstack/chirpstack-api-grpc-web/api/device_pb";
import { 
  ListDeviceProfilesRequest,
  ListDeviceProfilesResponse,
  DeviceProfileListItem,
} from "@chirpstack/chirpstack-api-grpc-web/api/device_profile_pb";
import type {
  ListMulticastGroupsResponse,
  MulticastGroupListItem,
} from "@chirpstack/chirpstack-api-grpc-web/api/multicast_group_pb";
import {
  ListMulticastGroupsRequest,
  AddDeviceToMulticastGroupRequest,
} from "@chirpstack/chirpstack-api-grpc-web/api/multicast_group_pb";
import type { ListRelaysResponse, RelayListItem } from "@chirpstack/chirpstack-api-grpc-web/api/relay_pb";
import { ListRelaysRequest, AddRelayDeviceRequest } from "@chirpstack/chirpstack-api-grpc-web/api/relay_pb";
import type {
  ListFuotaDeploymentsResponse,
  FuotaDeploymentListItem,
} from "@chirpstack/chirpstack-api-grpc-web/api/fuota_pb";
import {
  ListFuotaDeploymentsRequest,
  AddDevicesToFuotaDeploymentRequest,
} from "@chirpstack/chirpstack-api-grpc-web/api/fuota_pb";

import type { GetPageCallbackFunc } from "../../components/DataTable";
import DataTable from "../../components/DataTable";
import DeviceStore from "../../stores/DeviceStore";
import ApplicationStore from "../../stores/ApplicationStore";
import DeviceProfileStore from "../../stores/DeviceProfileStore";
import MulticastGroupStore from "../../stores/MulticastGroupStore";
import FuotaStore from "../../stores/FuotaStore";
import RelayStore from "../../stores/RelayStore";
import Admin from "../../components/Admin";

interface IProps {
  application: Application;
}

function ListDevices(props: IProps) {
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [multicastGroups, setMulticastGroups] = useState<MulticastGroupListItem[]>([]);
  const [relays, setRelays] = useState<RelayListItem[]>([]);
  const [fuotaDeployments, setFuotaDeployments] = useState<FuotaDeploymentListItem[]>([]);
  const [mgModalVisible, setMgModalVisible] = useState<boolean>(false);
  const [fuotaModalVisible, setFuotaModalVisible] = useState<boolean>(false);
  const [relayModalVisible, setRelayModalVisible] = useState<boolean>(false);
  const [mgSelected, setMgSelected] = useState<string>("");
  const [fuotaDeploymentSelected, setFuotaDeploymentSelected] = useState<string>("");
  const [relaySelected, setRelaySelected] = useState<string>("");
  const [applicationDeviceProfiles, setApplicationDeviceProfiles] = useState<ApplicationDeviceProfileListItem[]>([]);
  const [applicationDeviceTags, setApplicationDeviceTags] = useState<ApplicationDeviceTagListItem[]>([]);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  // Device filter state
  const [filterActivityStatus, setFilterActivityStatus] = useState<string>("");
  const [filterBatteryStatus, setFilterBatteryStatus] = useState<string>("");
  const [filterMarginRange, setFilterMarginRange] = useState<string>("");
  const [filterKey, setFilterKey] = useState<number>(0);

  useEffect(() => {
    const appDpReq = new ListApplicationDeviceProfilesRequest();
    appDpReq.setApplicationId(props.application.getId());
    ApplicationStore.listDeviceProfiles(appDpReq, (resp: ListApplicationDeviceProfilesResponse) => {
      setApplicationDeviceProfiles(resp.getResultList());
    });

    const appDevTagsReq = new ListApplicationDeviceTagsRequest();
    appDevTagsReq.setApplicationId(props.application.getId());
    ApplicationStore.listDeviceTags(appDevTagsReq, (resp: ListApplicationDeviceTagsResponse) => {
      setApplicationDeviceTags(resp.getResultList());
    });

    const mgReq = new ListMulticastGroupsRequest();
    mgReq.setLimit(999);
    mgReq.setApplicationId(props.application.getId());
    MulticastGroupStore.list(mgReq, (resp: ListMulticastGroupsResponse) => {
      setMulticastGroups(resp.getResultList());
    });

    const relayReq = new ListRelaysRequest();
    relayReq.setLimit(999);
    relayReq.setApplicationId(props.application.getId());
    RelayStore.list(relayReq, (resp: ListRelaysResponse) => {
      setRelays(resp.getResultList());
    });

    const fuotaReq = new ListFuotaDeploymentsRequest();
    fuotaReq.setLimit(999);
    fuotaReq.setApplicationId(props.application.getId());
    FuotaStore.listDeployments(fuotaReq, (resp: ListFuotaDeploymentsResponse) => {
      setFuotaDeployments(resp.getResultList());
    });
  }, [props]);

  const columns: ColumnsType<DeviceListItem.AsObject> = [
    {
      title: "Last seen",
      dataIndex: "lastSeenAt",
      key: "lastSeenAt",
      width: 250,
      render: (_text, record) => {
        if (record.lastSeenAt !== undefined) {
          const ts = new Date(0);
          ts.setUTCSeconds(record.lastSeenAt.seconds);
          return format(ts, "yyyy-MM-dd HH:mm:ss");
        }
        return "Never";
      },
      sorter: true,
    },
    {
      title: "DevEUI",
      dataIndex: "devEui",
      key: "devEui",
      width: 250,
      render: (text, record) => (
        <Link
          to={`/tenants/${props.application.getTenantId()}/applications/${props.application.getId()}/devices/${
            record.devEui
          }`}
        >
          {text}
        </Link>
      ),
      sorter: true,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
    },
    {
      title: "Device profile",
      dataIndex: "deviceProfileName",
      key: "deviceProfileId",
      render: (text, record) => (
        <Link to={`/tenants/${props.application.getTenantId()}/device-profiles/${record.deviceProfileId}/edit`}>
          {text}
        </Link>
      ),
      sorter: true,
      filterMultiple: false,
      filters: applicationDeviceProfiles.map(v => {
        return {
          text: v.getName(),
          value: v.getId(),
        };
      }),
    },
    {
      title: "Tags",
      dataIndex: "tagsMap",
      key: "tags",
      render: (text, record) => (
        <>
          {text.map((v: string[]) => (
            <Popover content={v[1]}>
              <Tag>{v[0]}</Tag>
            </Popover>
          ))}
        </>
      ),
      filterMultiple: false,
      filters: applicationDeviceTags.map(v => {
        return {
          text: v.getKey(),
          value: v.getKey(),
          children: v.getValuesList().map(vv => {
            return {
              text: vv,
              value: `${v.getKey()}=${vv}`,
            };
          }),
        };
      }),
    },
    {
      title: "Battery",
      dataIndex: "deviceStatus",
      key: "deviceStatus",
      width: 50,
      render: (_text, record) => {
        if (record.deviceStatus === undefined) {
          return;
        }

        if (record.deviceStatus.externalPowerSource === true) {
          return <FontAwesomeIcon icon={faPlug} />;
        } else if (record.deviceStatus.batteryLevel > 75) {
          return <FontAwesomeIcon icon={faBatteryFull} />;
        } else if (record.deviceStatus.batteryLevel > 50) {
          return <FontAwesomeIcon icon={faBatteryThreeQuarters} />;
        } else if (record.deviceStatus.batteryLevel > 25) {
          return <FontAwesomeIcon icon={faBatteryHalf} />;
        } else if (record.deviceStatus.batteryLevel > 0) {
          return <FontAwesomeIcon icon={faBatteryQuarter} />;
        }
      },
    },
  ];

  const getPage = (
    limit: number,
    offset: number,
    filters: object,
    orderBy: string | void,
    orderByDesc: boolean | void,
    callbackFunc: GetPageCallbackFunc,
  ) => {
    let f = filters as any;

    function getOrderBy(orderBy: string | void): ListDevicesRequest.OrderBy {
      switch (orderBy) {
        case "lastSeenAt":
          return ListDevicesRequest.OrderBy.LAST_SEEN_AT;
        case "deviceProfileName":
          return ListDevicesRequest.OrderBy.DEVICE_PROFILE_NAME;
        case "devEui":
          return ListDevicesRequest.OrderBy.DEV_EUI;
        default:
          return ListDevicesRequest.OrderBy.NAME;
      }
    }

    function getDeviceProfileId(filters: any): string {
      let dpIdFilter = filters.deviceProfileId;
      if (Array.isArray(dpIdFilter) && dpIdFilter.length > 0) {
        return dpIdFilter[0] as string;
      }

      return "";
    }

    const req = new ListDevicesRequest();
    req.setApplicationId(props.application.getId());
    req.setDeviceProfileId(getDeviceProfileId(f));
    req.setLimit(limit);
    req.setOffset(offset);
    req.setOrderBy(getOrderBy(orderBy));
    req.setOrderByDesc(orderByDesc || false);

    {
      let tagsFilter = f.tags;
      if (Array.isArray(tagsFilter)) {
        tagsFilter.forEach((v: string) => {
          const parts = v.split("=");
          req.getTagsMap().set(parts[0], parts[1]);
        });
      }
    }

    // Apply device filter bar state
    if (filterActivityStatus) {
      req.setActivityStatus(filterActivityStatus);
    }
    if (filterBatteryStatus) {
      req.setBatteryStatus(filterBatteryStatus);
    }
    if (filterMarginRange) {
      const [minStr, maxStr] = filterMarginRange.split(",");
      req.setMarginMin(parseInt(minStr, 10));
      req.setMarginMax(parseInt(maxStr, 10));
      req.setMarginFilterEnabled(true);
    }

    DeviceStore.list(req, (resp: ListDevicesResponse) => {
      const obj = resp.toObject();
      callbackFunc(obj.totalCount, obj.resultList);
    });
  };

  const onRowsSelectChange = (ids: string[]) => {
    setSelectedRowIds(ids);
  };

  const showMgModal = () => {
    setMgModalVisible(true);
  };

  const showRelayModal = () => {
    setRelayModalVisible(true);
  };

  const hideMgModal = () => {
    setMgModalVisible(false);
  };

  const hideRelayModal = () => {
    setRelayModalVisible(false);
  };

  const onMgSelected = (value: string) => {
    setMgSelected(value);
  };

  const onRelaySelected = (value: string) => {
    setRelaySelected(value);
  };

  const handleMgModalOk = () => {
    for (const devEui of selectedRowIds) {
      const req = new AddDeviceToMulticastGroupRequest();
      req.setMulticastGroupId(mgSelected);
      req.setDevEui(devEui);

      MulticastGroupStore.addDevice(req, () => {});
    }

    setMgModalVisible(false);
  };

  const handleRelayModalOk = () => {
    for (const devEui of selectedRowIds) {
      const req = new AddRelayDeviceRequest();
      req.setRelayDevEui(relaySelected);
      req.setDeviceDevEui(devEui);

      RelayStore.addDevice(req, () => {});
    }

    setRelayModalVisible(false);
  };

  const handleFuotaModalOk = () => {
    const req = new AddDevicesToFuotaDeploymentRequest();
    req.setFuotaDeploymentId(fuotaDeploymentSelected);
    req.setDevEuisList(selectedRowIds);

    FuotaStore.addDevices(req, () => {});
    setFuotaModalVisible(false);
  };

  // 批量删除设备
  const showDeleteConfirm = () => {
    Modal.confirm({
      title: "确认删除设备",
      icon: <ExclamationCircleOutlined />,
      content: `您确定要删除选中的 ${selectedRowIds.length} 个设备吗？此操作不可恢复。`,
      okText: "删除",
      okType: "danger",
      cancelText: "取消",
      onOk() {
        handleDeleteDevices();
      },
    });
  };

  const handleDeleteDevices = async () => {
    if (selectedRowIds.length === 0) {
      return;
    }

    setDeleteLoading(true);
    let successCount = 0;
    let failedCount = 0;

    try {
      for (const devEui of selectedRowIds) {
        const req = new DeleteDeviceRequest();
        req.setDevEui(devEui);

        try {
          await new Promise<void>((resolve, reject) => {
            DeviceStore.delete(req, () => {
              successCount++;
              resolve();
            });
          });
        } catch (error) {
          console.error(`删除设备 ${devEui} 失败:`, error);
          failedCount++;
        }
      }

      if (successCount > 0) {
        message.success(`成功删除 ${successCount} 个设备`);
      }

      if (failedCount > 0) {
        message.error(`${failedCount} 个设备删除失败`);
      }

      // 清空选中的设备
      setSelectedRowIds([]);
      
      // 刷新设备列表
      window.location.reload();
    } finally {
      setDeleteLoading(false);
    }
  };

  // CSV导入相关状态
  const [importModalVisible, setImportModalVisible] = useState<boolean>(false);
  const [importLoading, setImportLoading] = useState<boolean>(false);
  const [importResults, setImportResults] = useState<{success: number, failed: number, errors: string[]}>({
    success: 0,
    failed: 0,
    errors: [],
  });

  // 导出设备为CSV
  const exportDevicesToCSV = async (selectedOnly: boolean) => {
    try {
      // 获取要导出的设备列表
      let devicesToExport: DeviceListItem.AsObject[] = [];
      
      if (selectedOnly && selectedRowIds.length > 0) {
        // 导出选中的设备
        for (const devEui of selectedRowIds) {
          const req = new GetDeviceRequest();
          req.setDevEui(devEui);
          
          try {
            const device = await new Promise<Device>((resolve, reject) => {
              DeviceStore.get(req, (resp: GetDeviceResponse) => {
                resolve(resp.getDevice()!);
              });
            });
            
            // 获取设备激活信息
            const activationReq = new GetDeviceActivationRequest();
            activationReq.setDevEui(devEui);
            
            const activation = await new Promise<DeviceActivation | undefined>((resolve, reject) => {
              DeviceStore.getActivation(activationReq, (resp: GetDeviceActivationResponse) => {
                resolve(resp.getDeviceActivation());
              });
            });
            
            // 获取设备密钥信息
            const keysReq = new GetDeviceKeysRequest();
            keysReq.setDevEui(devEui);
            
            let nwkKey = "";
            try {
              const keys = await new Promise<GetDeviceKeysResponse | undefined>((resolve, reject) => {
                DeviceStore.getKeys(keysReq, (resp) => {
                  resolve(resp);
                });
              });
              
              if (keys && keys.getDeviceKeys()) {
                nwkKey = keys.getDeviceKeys()!.getNwkKey();
              }
            } catch (error) {
              console.error(`Error fetching device keys for ${devEui}:`, error);
            }
            
            // 查找设备配置文件名称
            const deviceProfileId = device.getDeviceProfileId();
            const matchingProfile = applicationDeviceProfiles.find(
              profile => profile.getId() === deviceProfileId
            );
            
            // 构建导出数据
            const deviceObj = device.toObject();
            devicesToExport.push({
              ...deviceObj,
              deviceProfileName: matchingProfile ? matchingProfile.getName() : "",
              devAddr: activation ? activation.getDevAddr() : "",
              appSessionKey: activation ? activation.getAppSKey() : "",
              nwkSessionKey: activation ? activation.getNwkSEncKey() : "",
              nwkKey: nwkKey,
              fCntUp: activation ? activation.getFCntUp() : 0,
              fCntDown: activation ? activation.getNFCntDown() : 0,
            } as any);
          } catch (error) {
            console.error(`Error fetching device ${devEui}:`, error);
          }
        }
      } else {
        // 获取所有设备的完整信息
        // 注意：这里可能需要分页获取所有设备，当前实现可能不完整
        message.info("导出所有设备功能尚未完全实现，请选择设备后导出");
        return;
      }
      
      // 准备CSV数据
      const csvData = devicesToExport.map(device => ({
        deviceProfileName: device.deviceProfileName,
        name: device.name,
        devEui: device.devEui,
        joinEui: (device as any).joinEui || "",
        nwkKey: (device as any).nwkKey || "",
        isDisabled: ((device as any).isDisabled || false).toString(),
        devAddr: (device as any).devAddr || "",
        appSessionKey: (device as any).appSessionKey || "",
        nwkSessionKey: (device as any).nwkSessionKey || "",
        fCntUp: (device as any).fCntUp || "0",
        fCntDown: (device as any).fCntDown || "0",
        description: device.description || "",
      }));
      
      // 生成CSV
      const csv = Papa.unparse(csvData);
      
      // 创建下载链接
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `devices_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success(`成功导出 ${csvData.length} 个设备`);
    } catch (error) {
      console.error("导出设备失败:", error);
      message.error("导出设备失败");
    }
  };

  // 处理CSV文件上传
  const handleFileUpload = async (file: File) => {
    setImportLoading(true);
    setImportResults({
      success: 0,
      failed: 0,
      errors: [],
    });
    
    // 获取所有设备配置文件，用于设备配置文件名称的匹配
    let allProfiles: DeviceProfileListItem[] = [];
    try {
      // 获取所有设备配置文件
      allProfiles = await new Promise<DeviceProfileListItem[]>((resolve, reject) => {
        const dpReq = new ListDeviceProfilesRequest();
        dpReq.setLimit(999); // 设置一个较大的限制，以获取所有设备配置文件
        dpReq.setTenantId(props.application.getTenantId());
        
        DeviceProfileStore.list(dpReq, (resp: ListDeviceProfilesResponse) => {
          resolve(resp.getResultList());
        });
      });
      
      // 如果没有设备配置文件，显示错误消息
      if (allProfiles.length === 0) {
        setImportResults({
          success: 0,
          failed: 0,
          errors: ["没有可用的设备配置文件。请先创建至少一个设备配置文件，然后再尝试导入设备。"],
        });
        setImportLoading(false);
        return false;
      }
    } catch (error) {
      setImportResults({
        success: 0,
        failed: 0,
        errors: ["获取设备配置文件失败。请刷新页面后重试。"],
      });
      setImportLoading(false);
      return false;
    }
    
    // 尝试自动检测分隔符
    Papa.parse(file, {
      header: true,
      delimiter: "", // 自动检测分隔符
      skipEmptyLines: true, // 跳过空行
      complete: async (results: Papa.ParseResult<any>) => {
        const { data, errors } = results;
        let successCount = 0;
        let failedCount = 0;
        const errorMessages: string[] = [];
        
        // 处理解析错误
        if (errors.length > 0) {
          // 检查是否是字段数量不匹配的错误
          const fieldCountErrors = errors.filter(e => e.message.includes("Too few fields") || e.message.includes("Too many fields"));
          if (fieldCountErrors.length > 0) {
            setImportResults({
              success: 0,
              failed: 0,
              errors: [
                "CSV格式错误：字段数量不匹配。请检查CSV文件格式是否正确，确保分隔符是逗号(,)。",
                "如果您使用Excel导出CSV，请确保选择'CSV (逗号分隔)(*.csv)'格式。",
                ...errors.map(e => `行 ${e.row}: ${e.message}`)
              ],
            });
          } else {
            setImportResults({
              success: 0,
              failed: 0,
              errors: errors.map(e => `行 ${e.row}: ${e.message}`),
            });
          }
          setImportLoading(false);
          return;
        }
        
        // 处理每一行数据
        for (const row of data as any[]) {
          if (!row.devEui) {
            continue; // 跳过空行
          }
          
          try {
            // 根据deviceProfileName查找对应的deviceProfileId
            let deviceProfileId = "";
            if (row.deviceProfileId) {
              deviceProfileId = row.deviceProfileId;
            } else if (row.deviceProfileName || row.applicationName) {
              row.deviceProfileName = row.deviceProfileName || row.applicationName;
              // 清理名称，去除前后空格
              const cleanName = row.deviceProfileName.trim();
              
              // 尝试精确匹配
              let matchingProfile = allProfiles.find(
                profile => profile.getName().toLowerCase() === cleanName.toLowerCase()
              );
              
              if (!matchingProfile) {
                // 如果仍然找不到，显示所有可用的设备配置文件名称
                const availableProfiles = allProfiles.map(p => p.getName()).join(", ");
                throw new Error(`找不到名称为 "${row.deviceProfileName}" 的设备配置文件。可用的设备配置文件: ${availableProfiles}`);
              }
              
              deviceProfileId = matchingProfile.getId();
            } else {
              throw new Error("缺少必填字段: deviceProfileName或applicationName");
            }
            
            // 创建或更新设备
            const device = new Device();
            device.setApplicationId(props.application.getId());
            device.setName(row.name || "");
            device.setDescription(row.description || ""); // description是可选的
            device.setDevEui(row.devEui);
            device.setJoinEui(row.joinEui || "");
            device.setDeviceProfileId(deviceProfileId);
            device.setIsDisabled(row.isDisabled === "true"); // isDisabled是可选的，默认为false
            
            // 尝试创建设备
            const createReq = new CreateDeviceRequest();
            createReq.setDevice(device);
            
            // 使用Promise包装DeviceStore.create，处理成功和错误情况
            try {
              await new Promise<void>((resolve, reject) => {
                DeviceStore.create(
                  createReq, 
                  () => {
                    // 成功创建设备
                    resolve();
                  },
                  (err) => {
                    // 设备创建失败，可能是因为设备已存在
                    reject(err);
                  }
                );
              });
            } catch (error) {
              // 如果设备已存在，继续处理激活信息
              console.log(`设备 ${row.devEui} 已存在，尝试更新设备信息`);
            }
            
            // 如果提供了nwkKey，则创建设备密钥
            if (row.nwkKey) {
              const deviceKeys = new DeviceKeys();
              deviceKeys.setDevEui(row.devEui);
              deviceKeys.setNwkKey(row.nwkKey);
              
              // 根据设备配置文件的MAC版本，可能需要设置不同的密钥
              // 这里简化处理，只设置NwkKey
              
              const createKeysReq = new CreateDeviceKeysRequest();
              createKeysReq.setDeviceKeys(deviceKeys);
              
              try {
                await new Promise<void>((resolve, reject) => {
                  DeviceStore.createKeys(createKeysReq, () => {
                    resolve();
                  });
                });
              } catch (error) {
                console.log(`设置设备 ${row.devEui} 的密钥失败，可能密钥已存在`);
              }
            }
            
            // 如果提供了激活信息，则激活设备
            if (row.devAddr && (row.appSessionKey || row.nwkSessionKey)) {
              const activation = new DeviceActivation();
              activation.setDevEui(row.devEui);
              activation.setDevAddr(row.devAddr);
              activation.setAppSKey(row.appSessionKey || "");
              activation.setNwkSEncKey(row.nwkSessionKey || "");
              // 设置其他密钥为相同的值
              activation.setSNwkSIntKey(row.nwkSessionKey || "");
              activation.setFNwkSIntKey(row.nwkSessionKey || "");
              
              // 设置帧计数器（可选）
              if (row.fCntUp) {
                activation.setFCntUp(parseInt(row.fCntUp, 10));
              }
              if (row.fCntDown) {
                activation.setNFCntDown(parseInt(row.fCntDown, 10));
              }
              
              const activateReq = new ActivateDeviceRequest();
              activateReq.setDeviceActivation(activation);
              
              await new Promise<void>((resolve, reject) => {
                DeviceStore.activate(activateReq, () => {
                  resolve();
                });
              });
            }
            
            successCount++;
          } catch (error: any) {
            failedCount++;
            errorMessages.push(`设备 ${row.devEui}: ${error.message || "未知错误"}`);
          }
        }
        
        setImportResults({
          success: successCount,
          failed: failedCount,
          errors: errorMessages,
        });
        setImportLoading(false);
        
        if (successCount > 0 && failedCount === 0) {
          message.success(`成功导入 ${successCount} 个设备`);
          setImportModalVisible(false);
        }
      },
      error: (error: Error) => {
        setImportResults({
          success: 0,
          failed: 0,
          errors: [error.message],
        });
        setImportLoading(false);
      }
    });
    
    return false; // 阻止自动上传
  };

  const menu = (
    <Menu>
      <Menu.Item onClick={showMgModal}>Add to multicast-group</Menu.Item>
      <Menu.Item onClick={() => setFuotaModalVisible(true)}>Add to FUOTA deployment</Menu.Item>
      <Menu.Item onClick={showRelayModal}>Add to relay</Menu.Item>
      <Menu.Divider />
      <Menu.Item onClick={() => exportDevicesToCSV(true)}>Export selected devices</Menu.Item>
      <Menu.Item onClick={() => showDeleteConfirm()}>Delete selected devices</Menu.Item>
    </Menu>
  );

  const mgOptions = multicastGroups.map(mg => <Select.Option value={mg.getId()}>{mg.getName()}</Select.Option>);

  const relayOptions = relays.map(r => <Select.Option value={r.getDevEui()}>{r.getName()}</Select.Option>);
  const fuotaOptions = fuotaDeployments.map(r => <Select.Option value={r.getId()}>{r.getName()}</Select.Option>);

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Modal
        title="Add selected devices to multicast-group"
        open={mgModalVisible}
        onOk={handleMgModalOk}
        onCancel={hideMgModal}
        okButtonProps={{ disabled: mgSelected === "" }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Select style={{ width: "100%" }} onChange={onMgSelected} placeholder="Select Multicast-group">
            {mgOptions}
          </Select>
        </Space>
      </Modal>
      <Modal
        title="Add selected devices to FUOTA deployment"
        open={fuotaModalVisible}
        onOk={handleFuotaModalOk}
        onCancel={() => setFuotaModalVisible(false)}
        okButtonProps={{ disabled: fuotaDeploymentSelected === "" }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Typography.Text>
            This will add the selected devices to a FUOTA deployment. Devices must have the same device-profile as
            associated with the FUOTA deployment.
          </Typography.Text>
          <Select
            style={{ width: "100%" }}
            onChange={v => setFuotaDeploymentSelected(v)}
            placeholder="Select FUOTA deployment"
          >
            {fuotaOptions}
          </Select>
        </Space>
      </Modal>
      <Modal
        title="Add selected devices to relay"
        open={relayModalVisible}
        onOk={handleRelayModalOk}
        onCancel={hideRelayModal}
        okButtonProps={{ disabled: relaySelected === "" }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Select style={{ width: "100%" }} onChange={onRelaySelected} placeholder="Select Relay">
            {relayOptions}
          </Select>
        </Space>
      </Modal>
      <Modal
        title="Import Devices"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setImportModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Typography.Paragraph>
            Upload a CSV file with device information. The CSV file should have the following columns:
            <br />
            <strong>Required:</strong> name, devEui, deviceProfileName (or applicationName)
            <br />
            <strong>Optional:</strong> joinEui, nwkKey, isDisabled, devAddr, appSessionKey, nwkSessionKey, fCntUp, fCntDown, description
          </Typography.Paragraph>
          
          <Upload
            beforeUpload={handleFileUpload}
            accept=".csv"
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />} loading={importLoading}>
              Select CSV File
            </Button>
          </Upload>
          
          {importResults.success > 0 && (
            <Typography.Text type="success">
              Successfully imported {importResults.success} devices.
            </Typography.Text>
          )}
          
          {importResults.failed > 0 && (
            <Typography.Text type="danger">
              Failed to import {importResults.failed} devices. Failed records are skipped and do not affect the import of other devices.
            </Typography.Text>
          )}
          
          {importResults.errors.length > 0 && (
            <div>
              <Typography.Text type="danger">Errors:</Typography.Text>
              <ul>
                {importResults.errors.slice(0, 10).map((error, i) => (
                  <li key={i}><Typography.Text type="danger">{error}</Typography.Text></li>
                ))}
                {importResults.errors.length > 10 && (
                  <li><Typography.Text type="danger">...and {importResults.errors.length - 10} more errors</Typography.Text></li>
                )}
              </ul>
            </div>
          )}
        </Space>
      </Modal>

      <Admin tenantId={props.application.getTenantId()} isDeviceAdmin>
        <Space direction="horizontal" style={{ float: "right" }}>
          <Button type="primary">
            <Link
              to={`/tenants/${props.application.getTenantId()}/applications/${props.application.getId()}/devices/create`}
            >
              Add device
            </Link>
          </Button>
          <Button onClick={() => setImportModalVisible(true)} icon={<UploadOutlined />}>
            Import devices
          </Button>
          <Dropdown placement="bottomRight" overlay={menu} trigger={["click"]} disabled={selectedRowIds.length === 0}>
            <Button>Selected devices</Button>
          </Dropdown>
        </Space>
      </Admin>
      <Row gutter={[16, 0]} style={{ marginBottom: 16 }} align="middle">
        <Col>
          <Space>
            <span style={{ whiteSpace: "nowrap" }}>Activity:</span>
            <Select
              allowClear
              placeholder="All"
              style={{ width: 140 }}
              value={filterActivityStatus || undefined}
              onChange={(v: string | undefined) => {
                setFilterActivityStatus(v || "");
                setFilterKey(k => k + 1);
              }}
            >
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="inactive">Inactive</Select.Option>
              <Select.Option value="never_seen">Never seen</Select.Option>
            </Select>
          </Space>
        </Col>
        <Col>
          <Space>
            <span style={{ whiteSpace: "nowrap" }}>Battery:</span>
            <Select
              allowClear
              placeholder="All"
              style={{ width: 140 }}
              value={filterBatteryStatus || undefined}
              onChange={(v: string | undefined) => {
                setFilterBatteryStatus(v || "");
                setFilterKey(k => k + 1);
              }}
            >
              <Select.Option value="external">External power</Select.Option>
              <Select.Option value="ok">Battery OK (&gt;25%)</Select.Option>
              <Select.Option value="low">Battery low (&le;25%)</Select.Option>
            </Select>
          </Space>
        </Col>
        <Col>
          <Space>
            <span style={{ whiteSpace: "nowrap" }}>Signal (SNR):</span>
            <Select
              allowClear
              placeholder="All"
              style={{ width: 160 }}
              value={filterMarginRange || undefined}
              onChange={(v: string | undefined) => {
                setFilterMarginRange(v || "");
                setFilterKey(k => k + 1);
              }}
            >
              <Select.Option value="10,32">Good (&ge;10 dB)</Select.Option>
              <Select.Option value="0,9">Fair (0–9 dB)</Select.Option>
              <Select.Option value="-32,-1">Poor (&lt;0 dB)</Select.Option>
            </Select>
          </Space>
        </Col>
      </Row>
      <DataTable columns={columns} getPage={getPage} onRowsSelectChange={onRowsSelectChange} rowKey="devEui" refreshKey={filterKey} />
    </Space>
  );
}

export default ListDevices;
